/**
 ShapeDiver 3D Viewer Application

 Tag3DLoader.js

 Content:
   Create Object3D from JSON tag3d description. Load font if necessary.

  Copyright (c) 2018 ShapeDiver GmbH

  @module Tag3DLoader
 */

// require threejs
var THREE = require('../../externals/three');

var toTinyColor = require('../util/toTinyColor');

// keep font object in cache once it is used for the first time
var _font3d = null;

/**
 * 3d text tag justification
 * @typedef {String} Tag3DJustification - The format for 3d tag justifications consists of two letters,
 * the first one for vertical (T, M, B) and the second one for horizontal (L, C, R) justification.
 */

/**
 * A vector
 * @typedef {Object} Tag3DVector
 * @property {Number} X
 * @property {Number} Y
 * @property {Number} Z
 */

/**
 * 3d tag location type, essentially a plane definition
 * @typedef {Object} Tag3DLocation
 * @property {module:Tag3DLoader~Tag3DVector} normal
 * @property {module:Tag3DLoader~Tag3DVector} origin
 * @property {module:Tag3DLoader~Tag3DVector} xAxis
 * @property {module:Tag3DLoader~Tag3DVector} yAxis
 *
 */

/**
 * tag3d JSON format
 * @typedef {Object} JSONTag3d
 * @property {String} color - Color in the hex format "#aabbcc"
 * @property {Tag3DJustification} justification - Justification of the tag
 * @property {module:Tag3DLoader~Tag3DLocation} location
 * @property {String} size - A float number string
 * @property {String} text - The text of the tag
 * @property {String} version - The tag format version
 */

/**
 * Creates an Object3D holding all 3d tags described in a JSON array
 * @param  {JSONTag3d[]} jsonTag3DArray Array of JSON 3d tag descriptions
 * @return {Promise<module:OutputVersion~GeometryData[]>} Definition of geometry of the 3d tags
 */
var load3DTags = function (jsonTag3DArray) {

  return new Promise(function(resolve) {
    if (_font3d == null) {
      let fontloader = new THREE.FontLoader();
      fontloader.load('https://viewer.shapediver.com/graphik_regular.typeface.json', function (response) {
        _font3d = response;
        resolve();
      }.bind(this));
    } else {
      resolve();
    }
  }).then(function() {

    // create array holding all tag meshes
    let tagArray = [];

    // go through all the tags in this tag array
    for (let tdIndex in jsonTag3DArray) {
      let tagDef = jsonTag3DArray[tdIndex];

      let tagParams = {
        size: tagDef.size,
        height: tagDef.size / 10,
        font: _font3d
      };

      // divide text up into lines
      let tagLines = tagDef.text.split(/\r\n|\r|\n/g);

      // create object which will hold all the lines of one tag
      let tagPathString = 'tag3d_' + tdIndex;

      let lineArray = [];

      //
      for (let lineIndex = 0; lineIndex < tagLines.length; ++lineIndex) {

        let linePathString = tagPathString + '.line_' + lineIndex;

        // create tag mesh object
        let tag = new THREE.TextBufferGeometry(tagLines[lineIndex], tagParams);
        let geometryDefinition = {
          path: linePathString,
          geometry: tag,
          texCoord: false,
          type: 'mesh'
        };

        if (tagDef.color != 'default') {
          geometryDefinition.color = toTinyColor(tagDef.color);
        }
        lineArray.push(geometryDefinition);
      }

      // create temporary object
      let parentObject = new THREE.Object3D();
      for (let line of lineArray) {
        parentObject.add(new THREE.Mesh(line.geometry, new THREE.MeshPhongMaterial()));
      }

      // align lines
      let bb, extentsX, extentsY, lineHeight;
      {
        lineHeight = 0;
        for (let child of parentObject.children) {
          bb = new THREE.Box3().setFromObject(child);
          extentsY = bb.max.y - bb.min.y;
          lineHeight = Math.max(lineHeight,extentsY);
        }
      }
      lineHeight *= 1.15;

      lineArray.forEach( (line, i) => {
        line.geometry.translate(0,(-i-1)*lineHeight,0);
      });

      // justification
      bb = new THREE.Box3().setFromObject(parentObject);

      extentsX = bb.max.x - bb.min.x;
      extentsY = bb.max.y - bb.min.y;
      var tagJustTranslation = new THREE.Vector3(0, 0, 0);

      switch (tagDef.justification) {
      case 'TL':
        break;
      case 'TC':
        tagJustTranslation.x = -extentsX * 0.5;
        break;
      case 'TR':
        tagJustTranslation.x = -extentsX;
        break;
      case 'ML':
        tagJustTranslation.y = extentsY * 0.5;
        break;
      case 'MC':
        tagJustTranslation.x = -extentsX * 0.5;
        tagJustTranslation.y = extentsY * 0.5;
        break;
      case 'MR':
        tagJustTranslation.x = -extentsX;
        tagJustTranslation.y = extentsY * 0.5;
        break;
      case 'BL':
        tagJustTranslation.y = extentsY;
        break;
      case 'BC':
        tagJustTranslation.x = -extentsX * 0.5;
        tagJustTranslation.y = extentsY;
        break;
      case 'BR':
        tagJustTranslation.x = -extentsX;
        tagJustTranslation.y = extentsY;
        break;
      }

      for (let line of lineArray) {
        line.geometry.translate(tagJustTranslation.x,tagJustTranslation.y,tagJustTranslation.z);
      }

      // rotation
      if (tagDef.location.hasOwnProperty('xAxis')) {
        var rotMatrix = new THREE.Matrix4();
        rotMatrix.set(tagDef.location.xAxis.X, tagDef.location.yAxis.X, tagDef.location.normal.X, 0, tagDef.location.xAxis.Y, tagDef.location.yAxis.Y, tagDef.location.normal.Y, 0, tagDef.location.xAxis.Z, tagDef.location.yAxis.Z, tagDef.location.normal.Z, 0, 0, 0, 0, 1);
        for (let line of lineArray) {
          line.geometry.applyMatrix(rotMatrix);
          line.geometry.translate(tagDef.location.origin.X, tagDef.location.origin.Y, tagDef.location.origin.Z);
        }
      }

      // add lines to global tag array
      Array.prototype.push.apply(tagArray, lineArray);

    }
    return tagArray;
  });
};

module.exports = {load3DTags: load3DTags};
